<script setup lang="ts">
import { computed, inject, ref, toRefs } from 'vue';
import DocumentImage from '@/components/common/adp-document/basic-info/document-image.vue';
import DocumentTitle from '@/components/common/adp-document/basic-info/document-title.vue';
import { AdpDocumentBasicInfo } from '@/types';
import { formatDate } from '@/utils/formatters';
import { visibleAdpCardKey } from '@/utils/injectionKeys';

interface Props {
  adpDocument: AdpDocumentBasicInfo;
  // 文字サイズと余白のみ
  size?: 's' | 'xm' | 'm' | 'l';
  // banner は画像をタイトルの上に、thumbnail は右側に配置する
  imageVariant?: 'banner' | 'thumbnail';
  bannerRatio?: '3to1' | '16to9';
  isNarrow?: boolean;
  isRelatedArticle?: boolean;
  showPublishedDate?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'xm',
  imageVariant: 'thumbnail',
  bannerRatio: '3to1',
  isNarrow: false,
  isRelatedArticle: false,
  showPublishedDate: true,
});

const emit = defineEmits<{
  'article-link-clicked': [clickedMainTitle?: boolean];
  'article-link-context-menu': [event: Event];
}>();

const { adpDocument } = toRefs(props);

// 親（AdpDocumentCard）が表示監視している場合のみ可視判定が渡ってくる。
// それ以外（記事詳細・関連記事など）ではタイトルのツールチップを出さない。
const visibleAdpCard = inject(visibleAdpCardKey, ref(false));

const publishedAt = computed(() =>
  props.adpDocument.published_at
    ? formatDate(props.adpDocument.published_at)
    : undefined,
);

const thumbnailSize = computed(() => (props.size === 'l' ? 'm' : props.size));

const onLinkClicked = (clickedMainTitle: boolean = false) => {
  adpDocument.value.is_read = true;
  emit('article-link-clicked', clickedMainTitle);
};
</script>

<template>
  <div
    class="document-basic-info"
    :class="[
      `size-${size}`,
      {
        'is-banner': imageVariant === 'banner',
        'is-thumbnail': imageVariant === 'thumbnail',
        'document-basic-info-related-articles': isRelatedArticle,
      },
    ]"
  >
    <div class="document-info-image">
      <DocumentImage
        v-if="imageVariant === 'banner'"
        variant="banner"
        :banner-ratio="bannerRatio"
        :adp-document="adpDocument"
        @article-link-clicked="onLinkClicked(false)"
        @article-link-context-menu="emit('article-link-context-menu', $event)"
      />
      <div class="document-info-box">
        <div class="document-sub-info">
          <slot name="publisher" />
        </div>
        <DocumentTitle
          :adp-document="adpDocument"
          :size="size"
          :max-lines="isNarrow ? 3 : 4"
          :enable-tooltip="visibleAdpCard"
          @article-link-clicked="onLinkClicked"
          @article-link-context-menu="emit('article-link-context-menu', $event)"
        />
        <slot name="afterTitle" />
        <div class="document-sub-info">
          <div
            v-if="publishedAt && showPublishedDate"
            class="document-date c-text-regular--s"
          >
            {{ publishedAt }}
          </div>
          <slot name="dateExtra" />
        </div>
        <div class="document-box-bottom">
          <slot name="boxBottom" />
        </div>
      </div>
      <DocumentImage
        v-if="imageVariant === 'thumbnail'"
        variant="thumbnail"
        :adp-document="adpDocument"
        :is-related-article="isRelatedArticle"
        :thumbnail-size="thumbnailSize"
        @article-link-clicked="onLinkClicked(false)"
        @article-link-context-menu="emit('article-link-context-menu', $event)"
      />
    </div>
    <slot name="bottom" />
  </div>
</template>

<style lang="scss" scoped>
.document-basic-info {
  width: 100%;

  .document-info-image:has(.image-link:hover) .document-title {
    text-decoration: underline;
  }

  .document-info-box {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .document-box-bottom:empty {
      display: none;
    }

    .document-sub-info {
      display: flex;
      flex-direction: row;
      color: $color-gray800;
      align-items: center;

      &:empty {
        display: none;
      }

      div.document-date {
        margin-right: 2px;
        white-space: nowrap;
      }
    }
  }

  &.is-thumbnail {
    .document-info-image {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;

      .document-info-box {
        flex: 1;
        min-width: 0;
      }
    }

    &.size-s .document-info-image {
      align-items: flex-start;
      gap: 12px;
    }
  }

  &.is-banner {
    .document-info-box {
      padding: 12px var(--document-inset, 0);
    }

    &.size-s .document-info-box {
      padding-bottom: 0;
    }
  }

  &.document-basic-info-related-articles {
    // 1行のタイトルと2行のタイトルは同じ高さにするため
    min-height: 94px;
  }
}
</style>
